import { Modal, Typography } from '@sb/cc-design-system';
import { useNavigate } from 'react-router-dom';
import { Snackbar } from 'src/components/snackbar';
import { PATHS } from 'src/constants/paths';
import { useUpdatePublication } from 'src/hooks';
import { lang, useUserRole } from 'src/utils';

import { PublicationModalProps } from './PublicationModal.interface';
import {
  PublishMessageContainer,
  PublishMessageLink,
  PublishMessageText,
} from './PublicationModal.styled';

/**
 * V2 of PublicationModal using the Modal component from cc-design-system
 * Maintains all the same functionality as the original PublicationModal
 */
export const PublicationModalV2 = (props: PublicationModalProps) => {
  const updatePublication = useUpdatePublication();
  const navigate = useNavigate();
  const { isOwnerOperator } = useUserRole();

  const onClickedViewStatus = async () => {
    navigate(PATHS.LIST_PUBLICATIONS);
  };

  const onClickedAddPublication = async () => {
    try {
      props.onPublished?.(true);
      props.onClose();

      Snackbar.info(
        <PublishMessageContainer>
          <PublishMessageText colorType="info" component="span">
            {lang.processPublishing}
          </PublishMessageText>
          {!isOwnerOperator && (
            <PublishMessageLink onClick={onClickedViewStatus} colorType="info">
              {lang.viewStatus}
            </PublishMessageLink>
          )}
        </PublishMessageContainer>,
      );

      await updatePublication.mutateAsync({
        locationCodes: props.locationCodes,
        locationGroupIDs: props.locationGroupIDs,
        publicationType: props.publicationType,
        assemblyIds: props.assemblyIds,
      });
      Snackbar.success(
        <PublishMessageContainer>
          <PublishMessageText component="span">
            {props.publishMessage
              ? props.publishMessage
              : props.successMessage ?? lang.successfullyPublished}
          </PublishMessageText>
          {!isOwnerOperator && (
            <PublishMessageLink onClick={onClickedViewStatus}>
              {lang.viewStatus}
            </PublishMessageLink>
          )}
        </PublishMessageContainer>,
      );
    } catch (error) {
      // Error handling is implicit - user sees the modal stays open with loading state
    } finally {
      props.onPublished?.(false);
    }
  };

  const handleCancel = () => {
    if (!updatePublication.isLoading) {
      if (props.closeMessage) {
        Snackbar.success(props.closeMessage);
      }
      props.onClose();
    }
  };

  // Map size values from MUI Breakpoint to cc-design-system sizeVariant
  // cc-design-system uses 'small' | 'medium' | 'large'
  const mapSizeVariant = (size?: string) => {
    const sizeMap: Record<string, 'small' | 'medium' | 'large'> = {
      xs: 'small',
      sm: 'small',
      md: 'medium',
      lg: 'large',
      xl: 'large',
    };

    return sizeMap[size || 'xs'] || 'small';
  };

  return (
    <Modal
      open={props.open}
      onClose={!updatePublication.isLoading ? props.onClose : () => {}}
      title={props.title ?? lang.publicationModal.title}
      disableBackdropClick={updatePublication.isLoading}
      sizeVariant={mapSizeVariant(props.modalSize)}
      confirmButtonText={props.confirmText ?? lang.publish}
      cancelButtonText={props.cancelText ?? lang.close}
      onConfirmAction={onClickedAddPublication}
      onCancelAction={handleCancel}
      cancelButtonProps={{ disabled: updatePublication.isLoading }}
      confirmButtonProps={{
        disabled: updatePublication.isLoading,
        loading: updatePublication.isLoading,
      }}
      headerStyle={{ padding: '0 16px 0 16px', alignItems: 'center' }}>
      <Typography variant="body2">
        {props.subTitle ?? lang.publicationModal.subTitle}
      </Typography>
    </Modal>
  );
};
